// ── AccountRow ───────────────────────────────────────────────────────────────
// A self-contained "Account" row for the Settings panel: shows who's signed in
// (avatar + name/email), a Sign out button, and a Delete account flow. Renders
// nothing when Firebase isn't configured, so local-only mode looks exactly
// like before.
import React, { useState } from 'react';
import { CONFIRM_STACK, CONFIRM_DANGER, CONFIRM_CANCEL } from '../theme/sharedStyles';
import { toast } from 'sonner';
import { isFirebaseConfigured } from './config';
import { useAuth, signOutUser, deleteAccountEverywhere } from './auth';
import { stopSync, wipeLocalData } from './sync';
import { usePremium } from './billing';
import { useRenewingPlaySubscription } from './playBilling';
import { clearAccountHint } from '../google/calendar';
import Modal from '../components/Modal';

export default function AccountRow() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  // Whether there's a LIVE Play subscription behind the current Premium
  // grant. Reads billing/status's `via` field (stamped by grantPremiumDays,
  // see lib/grantPremium.js) rather than asking the on-device Play Billing
  // plugin directly: the plugin's own `owned` flag is documented to stay
  // false until purchase receipts finish an async load+verify, which lags
  // noticeably behind this same Firestore doc right after a fresh purchase
  // — someone who buys Premium and immediately opens Settings would see no
  // warning at all under the plugin-based check, even though the purchase
  // already went through and billing/status already says so.
  //
  // A Lifetime grant also stamps via: 'play' but has nothing that bills, so
  // for Lifetime holders the warning only shows when the device still has a
  // subscription set to renew (bought before Lifetime and never cancelled).
  const { isPremium, via, lifetime } = usePremium();
  const oldSubscriptionRenewing = useRenewingPlaySubscription(lifetime);
  const subscribed = isPremium && via === 'play' && (!lifetime || oldSubscriptionRenewing);

  if (!isFirebaseConfigured || !user) return null;

  const name = user.displayName || user.email || 'Signed in';
  const initial = (user.displayName || user.email || '?').trim().charAt(0).toUpperCase();

  const handleSignOut = async () => {
    setBusy(true);
    try {
      stopSync();
      await signOutUser();
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await deleteAccountEverywhere();
      stopSync();
      wipeLocalData();
      clearAccountHint();
      toast.success('Your account and data have been deleted.');
      window.location.reload();
    } catch (e) {
      toast.error(e.message || 'Could not delete your account. Please try again.');
      setDeleting(false);
    }
  };

  return (
    <div style={{
      padding: '14px 20px', borderBottom: '1px solid var(--c-settings-line, #f5eaf0)',
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      {user.photoURL ? (
        <img src={user.photoURL} alt="" referrerPolicy="no-referrer"
          style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, objectFit: 'cover' }} />
      ) : (
        <div style={{
          width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))', color: 'var(--c-on-primary)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 16,
        }}>{initial}</div>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--c-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {name}
        </div>
        <div style={{ fontSize: 11, color: 'var(--c-text-soft)' }}>Synced across your devices</div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', flexShrink: 0 }}>
        <button
          onClick={handleSignOut}
          disabled={busy}
          style={{
            background: 'var(--c-bg-card)', color: 'var(--c-text-accent)',
            border: '1px solid var(--c-border)', borderRadius: 999,
            padding: '7px 14px', fontSize: 12, fontWeight: 700,
            cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? '…' : 'Sign out'}
        </button>
        <button
          onClick={() => { setConfirmText(''); setConfirming(true); }}
          style={{
            background: 'none', border: 'none', color: 'var(--c-text-soft)',
            fontSize: 11, textDecoration: 'underline', cursor: 'pointer', padding: 0,
          }}
        >
          Delete account
        </button>
      </div>

      {confirming && (
        <Modal onClose={() => !deleting && setConfirming(false)}>
          <div style={{
            background: 'var(--c-bg-card)', borderRadius: 20, padding: '24px 26px',
            width: 360, maxWidth: '100%', boxSizing: 'border-box',
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--c-text)', marginBottom: 8 }}>
              Delete your account?
            </div>
            <p style={{ fontSize: 13, color: 'var(--c-text-soft)', lineHeight: 1.5, marginTop: 0 }}>
              This permanently deletes your Clarity account, all synced goals/habits/tasks/diary
              data, and disconnects Google Calendar if connected. This cannot be undone.
            </p>
            {subscribed && (
              // Deleting the account does NOT cancel the Google Play
              // subscription — the app has no way to cancel it, only the user
              // does. Left unsaid, they'd keep being billed and reasonably
              // assume deleting had stopped it.
              <p style={{
                fontSize: 12, color: 'var(--c-text)', lineHeight: 1.5,
                background: 'var(--c-bg)', border: '1px solid var(--c-border)',
                borderRadius: 10, padding: '10px 12px', marginTop: 0, marginBottom: 14,
              }}>
                <strong>Your Premium subscription will keep billing.</strong> Deleting your
                Clarity account does not cancel it. You can only cancel it in the Play Store,
                under Payments &amp; subscriptions. Cancel there first if you want the billing
                to stop.
              </p>
            )}
            <p style={{ fontSize: 12, color: 'var(--c-text-soft)', marginBottom: 6 }}>
              Type <strong>DELETE</strong> to confirm:
            </p>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={deleting}
              autoFocus
              style={{
                width: '100%', boxSizing: 'border-box', padding: '8px 12px',
                borderRadius: 10, border: '1px solid var(--c-border)', fontSize: 14,
                marginBottom: 16,
              }}
            />
            <div style={CONFIRM_STACK}>
              <button
                onClick={() => setConfirming(false)}
                disabled={deleting}
                style={CONFIRM_CANCEL}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={confirmText !== 'DELETE' || deleting}
                style={{
                  ...CONFIRM_DANGER,
                  ...(confirmText === 'DELETE' ? {} : { background: 'var(--c-border)', boxShadow: 'none' }),
                  cursor: confirmText === 'DELETE' && !deleting ? 'pointer' : 'default',
                  opacity: deleting ? 0.7 : 1,
                }}
              >
                {deleting ? 'Deleting…' : 'Delete forever'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
